// =============================================================================
// MHV UTILS — Shared utility functions for Memory Hole Vintage
// Extends window.MHV (set up by mhv-config.js). Load AFTER mhv-config.js.
// =============================================================================

(function() {
  'use strict';

  var MHV = window.MHV || {};

  /**
   * Escape HTML entities to prevent XSS.
   * Used across: live page, host page, chat widget, auction hub.
   */
  MHV.escapeHtml = function(text) {
    if (!text) return '';
    var div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  /**
   * Quote-safe HTML escaper for ATTRIBUTE contexts. Escapes all five significant
   * chars INCLUDING quotes — unlike escapeHtml (text-context only, leaves " and '
   * raw). Use this for any value placed inside a quoted attribute (src, href,
   * value, title, data-*). For building whole fragments prefer the MHV.html
   * tagged template; use escapeAttr for one-off attribute interpolation.
   */
  MHV.escapeAttr = function(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  };

  // Escape all five HTML-significant chars — safe in BOTH element text and
  // quoted-attribute contexts (escapeHtml above does not escape quotes, so it's
  // unsafe inside attributes). Used internally by MHV.html.
  function escForHtml(v) {
    if (v === null || v === undefined) return '';
    return String(v).replace(/[&<>"']/g, function(c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /**
   * Mark a string as already-safe trusted HTML so MHV.html does NOT re-escape it.
   * Use ONLY for HTML you built yourself (e.g. nested MHV.html fragments) — never
   * for user/server data. This is the explicit, greppable opt-out.
   *   MHV.html`<div>${MHV.raw(trustedRowsHtml)}</div>`
   *
   * Returns a marker object that stringifies to the HTML (so `el.innerHTML = ...`
   * and string concatenation still work), but is recognized by MHV.html so nested
   * results aren't double-escaped.
   */
  MHV.raw = function(html) {
    var s = (html === null || html === undefined) ? '' : String(html);
    return { __mhvRaw: s, toString: function() { return s; } };
  };

  /**
   * Auto-escaping HTML tagged template — the safe-by-default way to build markup
   * for innerHTML. Every ${interpolation} is HTML-escaped automatically; you have
   * to go out of your way (MHV.raw) to inject unescaped HTML. This inverts the old
   * model where you had to remember MHV.escapeHtml() around every field.
   *
   *   el.innerHTML = MHV.html`<span class="name">${user.screenName}</span>`;       // escaped
   *   el.innerHTML = MHV.html`<ul>${rows.map(r => MHV.html`<li>${r.name}</li>`)}</ul>`; // arrays + nesting
   *
   * Arrays are joined; MHV.raw(...) values and nested MHV.html results pass through
   * unescaped (they're already safe). Returns an MHV.raw marker — assign it to
   * innerHTML or nest it directly; call String(result) if you need a plain string.
   */
  MHV.html = function(strings) {
    var values = Array.prototype.slice.call(arguments, 1);
    function render(v) {
      if (v === null || v === undefined) return '';
      if (typeof v === 'object' && v.__mhvRaw !== undefined) return v.__mhvRaw; // trusted (raw / nested html)
      if (Array.isArray(v)) return v.map(render).join('');
      return escForHtml(v);
    }
    var out = strings[0];
    for (var i = 0; i < values.length; i++) {
      out += render(values[i]) + strings[i + 1];
    }
    return MHV.raw(out);
  };

  /**
   * Determine if a given date falls within US Eastern Daylight Saving Time.
   * DST starts: second Sunday in March at 2:00 AM EST (07:00 UTC)
   * DST ends:   first Sunday in November at 2:00 AM EDT (06:00 UTC)
   */
  MHV.isEasternDST = function(year, month, day) {
    var march = new Date(Date.UTC(year, 2, 1));
    var marchDay = march.getUTCDay();
    var secondSundayMarch = 8 + ((7 - marchDay) % 7);

    var november = new Date(Date.UTC(year, 10, 1));
    var novemberDay = november.getUTCDay();
    var firstSundayNovember = (novemberDay === 0) ? 1 : (8 - novemberDay);

    var dstStart = new Date(Date.UTC(year, 2, secondSundayMarch, 7, 0, 0));
    var dstEnd = new Date(Date.UTC(year, 10, firstSundayNovember, 6, 0, 0));

    var inputDate = new Date(Date.UTC(year, month - 1, day));
    return (inputDate >= dstStart && inputDate < dstEnd);
  };

  /**
   * Convert Eastern local time (EST/EDT) to a UTC Date object.
   * Automatically handles DST offset (UTC-5 for EST, UTC-4 for EDT).
   *
   * @param {number} year   - 4-digit year
   * @param {number} month  - 1-based month (1=Jan, 12=Dec)
   * @param {number} day    - Day of month
   * @param {number} hour   - Hour (0-23) in Eastern time
   * @param {number} minute - Minute (0-59)
   * @returns {Date} UTC Date object
   */
  MHV.easternToUTC = function(year, month, day, hour, minute) {
    var inDST = MHV.isEasternDST(year, month, day);
    var offset = inDST ? 4 : 5;
    return new Date(Date.UTC(year, month - 1, day, hour + offset, minute, 0));
  };

  /**
   * Money for DISPLAY. Cents appear only when there ARE cents — a whole-dollar bid reads
   * "$25", not "$25.00". Thousands are comma-separated.
   * Accepts a number or an already-formatted string ("$1,234.00"), so it is safe to run
   * over a value that has been through another formatter. Returns '-' for non-numbers.
   * NOTE: display only — never use it to build a value that gets parsed or stored.
   */
  MHV.money = function(value) {
    var n = typeof value === 'number'
      ? value
      : parseFloat(String(value == null ? '' : value).replace(/[$,\s]/g, ''));
    if (!isFinite(n)) return '-';
    var parts = (n % 1 === 0 ? String(n) : n.toFixed(2)).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  };

  /**
   * Format a customer's real name as "First L." (last initial only).
   * Returns '' when no first name is available.
   */
  MHV.realName = function(firstName, lastName) {
    firstName = (firstName || '').trim();
    if (!firstName) return '';
    var last = (lastName || '').trim();
    return last ? firstName + ' ' + last.charAt(0).toUpperCase() + '.' : firstName;
  };

  /**
   * Resolve how a customer should appear in chat / viewer lists.
   * Model (2026, name-first): the REAL name (First L.) is the PRIMARY label, revealed
   * by default. A set screen name is ALWAYS shown — as the secondary line under the
   * real name, or AS the primary when the customer hides their real name (hideName).
   * Admins/employees always see the real name.
   *
   * Mirrors the backend lib/display-name.js — keep the two in sync.
   *
   * @returns {{primary: string, secondary: string, bidderFallback: boolean}}
   *   primary   — main name line ("First L." → screen name → "Bidder #n" → "Guest")
   *   secondary — the screen name shown UNDER the real name when revealed
   */
  MHV.chatDisplayName = function(p) {
    p = p || {};
    var sn = (p.screenName || '').trim();
    var real = MHV.realName(p.firstName, p.lastName);
    var reveal = !!p.isAdminView || !p.hideName;

    if (reveal && real) return { primary: real, secondary: sn, bidderFallback: false };
    if (sn) return { primary: sn, secondary: '', bidderFallback: false };
    if (p.bidderNumber) return { primary: 'Bidder #' + p.bidderNumber, secondary: '', bidderFallback: true };
    return { primary: 'Guest', secondary: '', bidderFallback: false };
  };

  /**
   * Canonical, guaranteed-non-empty identity label for a customer — used as the
   * stable key for lot requests (and matched against viewers for online status).
   * Resolves to screen name → "First L." → "Bidder #n". Always reveals the real
   * name (admin view) so requesters/viewers resolve consistently host-side.
   * Accepts a customer ({screenName,firstName,lastName,bidderNumber}) or a viewer
   * (whose screen name is in `name`). Commas are stripped (stored comma-joined).
   */
  MHV.requesterLabel = function(c) {
    if (!c) return '';
    var primary = MHV.chatDisplayName({
      screenName: c.screenName != null ? c.screenName : c.name,
      firstName: c.firstName, lastName: c.lastName,
      bidderNumber: c.bidderNumber, isAdminView: true
    }).primary;
    if (primary === 'Guest') return '';
    return primary.replace(/,/g, ' ').trim();
  };

  // Backdrop-dismiss guard: close a modal/overlay on a click ONLY when the mouse
  // press started on the backdrop itself — so a text-selection drag that begins in
  // a field and releases on the backdrop doesn't wrongly close the modal. Shared
  // by admin pages; mirrors the local versions in the table-core/scheduler libs.
  MHV.backdropDismiss = function(el, closeFn) {
    if (!el || typeof closeFn !== 'function') return;
    var down = false;
    el.addEventListener('mousedown', function(e) { down = (e.target === el); });
    el.addEventListener('click', function(e) {
      var ok = down; down = false;
      if (ok && e.target === el) closeFn();
    });
  };

  // Order the "Coming Up" lots. PRIMARY: request count DESC (most-wanted lots first).
  // WITHIN a count tier: STAGGER by the lot's primary requester (requesters[0] — the earliest,
  // who set the lot's request timestamp) so one person can't monopolize the top: group the
  // tier's lots by primary requester, order each requester's lots oldest-first, then round-robin
  // across requesters (rotation ordered by each requester's oldest request). A tier with a single
  // requester is therefore just oldest-first (no change). Lots with NO requests fall to the bottom,
  // ordered by lot number. Pure — returns a NEW array. The `requests` metafield is
  // "timestamp;count;requesters" (see auction-routes lot-request).
  MHV.orderComingUpLots = function(products, opts) {
    opts = opts || {};
    var getRequests = opts.getRequests || function(p) { return (p.metafields && p.metafields['requests']) || ''; };
    var getLot = opts.getLot || function(p) {
      var m = p.metafields || {};
      var lot = (m.custom && m.custom.lot) || m.lot;
      if (lot) return parseInt(lot, 10) || 0;
      var sku = p.variants && p.variants[0] && p.variants[0].sku;
      var mm = sku && String(sku).match(/LOT(\d+)/);
      if (mm) return parseInt(mm[1], 10) || 0;
      return parseInt(String(p.handle || '').replace(/\D/g, ''), 10) || 0;
    };
    var items = (products || []).map(function(p) {
      var parts = String(getRequests(p) || '').split(';');
      var reqs = (parts[2] || '').split(',').map(function(s) { return s.trim(); }).filter(Boolean);
      return {
        p: p,
        count: parseInt(parts[1], 10) || 0,
        ts: parts[0] ? (Date.parse(parts[0]) || 0) : 0,   // oldest request time for the lot
        requester: reqs[0] || '',                          // primary (earliest) requester
        lot: getLot(p)
      };
    });

    // Bucket by count tier, process tiers high→low.
    var tiers = {};
    items.forEach(function(it) { (tiers[it.count] = tiers[it.count] || []).push(it); });
    var counts = Object.keys(tiers).map(Number).sort(function(a, b) { return b - a; });

    var out = [];
    counts.forEach(function(c) {
      var tier = tiers[c];
      if (c <= 0) {
        // Unrequested lots → lot-number order, at the bottom.
        tier.sort(function(a, b) { return a.lot - b.lot; });
        tier.forEach(function(it) { out.push(it.p); });
        return;
      }
      // Group by primary requester; oldest-first within each; round-robin across requesters.
      var byReq = {};
      tier.forEach(function(it) { (byReq[it.requester] = byReq[it.requester] || []).push(it); });
      Object.keys(byReq).forEach(function(r) {
        byReq[r].sort(function(a, b) { return (a.ts - b.ts) || (a.lot - b.lot); });
      });
      var reqOrder = Object.keys(byReq).sort(function(a, b) {
        return (byReq[a][0].ts - byReq[b][0].ts) || (byReq[a][0].lot - byReq[b][0].lot);
      });
      var more = true;
      while (more) {
        more = false;
        reqOrder.forEach(function(r) { if (byReq[r].length) { out.push(byReq[r].shift().p); more = true; } });
      }
    });
    return out;
  };

  // ── THE "Lot ### - " PREFIX ───────────────────────────────────────────────
  //
  // Every catalogue title is composed as "Lot ### - Title" (computeTitle in
  // routes/catalogue/helpers.js). Inside a UI that already labels the lot, that
  // prints the number twice and the word for no one; in a bare string that
  // travels alone — a chat announcement, a receipt line — it is what makes the
  // string self-describing.
  //
  // So this is a DISPLAY helper, and the rule for using it is: strip where the
  // surface around it already says which lot this is, keep it everywhere else.
  //
  // ⚠️ NEVER strip a title that is about to be STORED or SENT. On the host page
  // the displayed title is read back as data (getSellingProductTitle feeds the
  // sold-list entry on both commit and pass), so stripping the element there
  // would strip the record too. See TODO.md #5.
  //
  // Tolerates the hyphen, en dash and em dash, because titles have been written
  // by three different composers over time.
  // Take the "Lot ### - " off the front of a composed title, for DISPLAY only.
  //
  // ⚠️ THIS WAS BROKEN FROM THE DAY IT WAS WRITTEN and nothing caught it. The
  // pattern read /^s*Lots+S+s*[-–—]s*/i — every backslash had been eaten by the
  // shell that wrote the file, so it matched the literal text "s*Lots+S+s*" and
  // therefore nothing at all. Every caller looked correct, ran, and silently did
  // nothing; the prefix stayed on screen everywhere while the code said it was
  // gone. If you edit this function, edit the FILE — do not pipe it through a
  // shell.
  //
  // Deliberately conservative. `lot\b` will not match "Lottery", and requiring a
  // real separator means "Lot of 5 chairs" — an actual auction phrase — is left
  // alone. The app composes `Lot ${padded} - ${title}`; the extra shapes here
  // (#, colon) are for titles typed by hand.
  MHV.stripLotPrefix = function(title) {
    if (title == null) return '';
    return String(title).replace(/^\s*lot\b\s*#?\s*\S+\s*[-–—:]\s*/i, '');
  };

  window.MHV = MHV;
})();
